import { useEffect, useMemo, useRef, useState } from "react";
import { useDataProvider } from "react-admin";
import { SerialFraudIndicatorState } from "./serialFraudDashboard";

type QueueRow = {
  id?: string | number | null;
};

type FraudFlagRecord = {
  id?: string | number | null;
  applicationId?: string | number | null;
  hasFraudRisk?: SerialFraudIndicatorState;
};

const APPLICATION_FRAUD_FLAGS_BATCH_SIZE = 100;

const chunkApplicationIds = (applicationIds: string[]) => {
  const chunks: string[][] = [];
  for (let index = 0; index < applicationIds.length; index += APPLICATION_FRAUD_FLAGS_BATCH_SIZE) {
    chunks.push(applicationIds.slice(index, index + APPLICATION_FRAUD_FLAGS_BATCH_SIZE));
  }

  return chunks;
};
const buildUnknownFraudFlagRecords = (applicationIds: string[]): FraudFlagRecord[] =>
  applicationIds.map((applicationId) => ({
    id: applicationId,
    applicationId,
    hasFraudRisk: null,
  }));

const fetchFraudFlagBatches = async (
  applicationIds: string[],
  getMany: (resource: string, params: { ids: string[] }) => Promise<{ data?: FraudFlagRecord[] }>
) => {
  const records: FraudFlagRecord[] = [];

  for (const ids of chunkApplicationIds(applicationIds)) {
    try {
      const response = await getMany("applicationFraudFlags", { ids });
      records.push(...(response?.data || []));
    } catch (_error) {
      records.push(...buildUnknownFraudFlagRecords(ids));
    }
  }

  return records;
};

const normalizeFraudState = (value: unknown): SerialFraudIndicatorState => {
  if (value === true) {
    return true;
  }

  if (value === false) {
    return false;
  }

  return null;
};

const buildFraudFlagsById = (
  applicationIds: string[],
  records: FraudFlagRecord[] = []
): Record<string, SerialFraudIndicatorState> => {
  const responseByApplicationId = new Map<string, FraudFlagRecord>();
  records.forEach((record) => {
    const applicationId = String(record?.applicationId ?? record?.id ?? "");
    if (applicationId) {
      responseByApplicationId.set(applicationId, record);
    }
  });

  return applicationIds.reduce((acc, applicationId) => {
    const record = responseByApplicationId.get(applicationId);
    acc[applicationId] = record ? normalizeFraudState(record.hasFraudRisk) : null;
    return acc;
  }, {} as Record<string, SerialFraudIndicatorState>);
};

export const getVisibleApplicationIds = (rows: QueueRow[] = []) =>
  Array.from(
    new Set(
      (Array.isArray(rows) ? rows : [])
        .map((record) => record?.id)
        .filter((applicationId) => applicationId !== undefined && applicationId !== null)
        .map((applicationId) => String(applicationId))
    )
  );

export const useApplicationFraudFlags = ({
  canView,
  rows,
  isFetching,
}: {
  canView: boolean;
  rows: QueueRow[];
  isFetching?: boolean;
}) => {
  const dataProvider = useDataProvider();
  const getMany = dataProvider?.getMany;
  const [fraudFlagsByApplicationId, setFraudFlagsByApplicationId] = useState<
    Record<string, SerialFraudIndicatorState>
  >({});
  const lastRequestKeyRef = useRef<string | null>(null);
  const requestSequenceRef = useRef(0);
  const wasFetchingRef = useRef(false);
  const settleRequestVersionRef = useRef(0);

  const applicationIds = useMemo(() => getVisibleApplicationIds(rows), [rows]);
  const visibleIdsKey = applicationIds.join(",");

  useEffect(() => {
    const wasFetching = wasFetchingRef.current;
    const currentlyFetching = Boolean(isFetching);
    const justSettled = wasFetching && !currentlyFetching;
    wasFetchingRef.current = currentlyFetching;

    if (!canView || applicationIds.length === 0 || typeof getMany !== "function") {
      lastRequestKeyRef.current = null;
      setFraudFlagsByApplicationId((currentFlags) =>
        Object.keys(currentFlags).length === 0 ? currentFlags : {}
      );
      return undefined;
    }

    if (currentlyFetching) {
      return undefined;
    }

    if (justSettled) {
      settleRequestVersionRef.current += 1;
    }

    const requestKey = `${visibleIdsKey}|${settleRequestVersionRef.current}`;
    if (lastRequestKeyRef.current === requestKey) {
      return undefined;
    }

    lastRequestKeyRef.current = requestKey;
    const requestSequence = requestSequenceRef.current + 1;
    requestSequenceRef.current = requestSequence;

    fetchFraudFlagBatches(applicationIds, getMany)
      .then((records) => {
        if (requestSequenceRef.current !== requestSequence) {
          return;
        }

        setFraudFlagsByApplicationId(buildFraudFlagsById(applicationIds, records));
      })
      .catch(() => {
        if (requestSequenceRef.current === requestSequence) {
          lastRequestKeyRef.current = null;
        }
      });

    return undefined;
  }, [applicationIds, canView, getMany, isFetching, visibleIdsKey]);

  return fraudFlagsByApplicationId;
};
